import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import RouterPropTypes from 'react-router-prop-types';
import { Container } from 'reactstrap';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { Redirect } from 'react-router';
import classNames from 'classnames';

import Page from '../../components/layout/Page';
import HelperNavigation from '../../components/common/HelperNavigation';
import ProductPageContent from '../../components/product/ProductPageContent';
import { modelOf } from '../../prop-types';
import generatePath from '../../util/generatePath';
import ProductClass from '../../types/ProductClass';
import ProductStore from '../../store/ProductStore';
import CartStore from '../../store/CartStore';
import CategoryStore from '../../store/CategoryStore';
import Analytics from '../../analytics/Analytics';
import { addParametersToPath, parse } from '../../util/queryString';
import SectionStore from '../../store/SectionStore';
import RouteService from '../../services/RouteService';
import ContentForState from '../../components/loader/ContentForState';
import SidebarLayout from '../../components/layout/SidebarLayout';
import DefaultSidebar from '../../components/sidebar/DefaultSidebar';
import ContentPopup from '../../components/ad/ContentPopup';
import ScrollReset from '../../components/common/ScrollReset';
import ShareButtons from '../../components/common/ShareButtons';
import { LocationContextPropType } from '../../services/LocationContext';
import NavigationAddToCartRow from '../../components/header/NavigationAddToCartRow';
import {
  fixPercentDecodingForParam,
  pathFragmentMatchesLocation
} from '../../util/url';
import GenericMeta from '../../components/head/GenericMeta';
import TrackingStore from '../../store/TrackingStore';
import TrackingEventType from '../../types/TrackingPageType';
import ConfigStore from '../../store/ConfigStore';
import RequestState from '../../types/RequestState';
import CommonPage from '../../types/CommonPage';

@observer
class ProductPage extends Component {
  constructor(props) {
    super(props);
    this.maybeLoadProduct(this.getProductId());
  }

  componentDidMount() {
    // Another component may have reset the active category, set it always
    // after mount.
    this.maybeUpdateActiveCategory();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.maybeLoadProduct(this.getProductId());
    }

    if (
      prevProps.match.params.activeProductId !==
      this.props.match.params.activeProductId
    ) {
      this.sendTrackingData();
    }
  }

  componentWillUnmount() {
    const { categoryStore } = this.props;
    // Clean-up active category
    categoryStore.setActiveCategory(null);
  }

  maybeLoadProduct = (id) => {
    const { productStore, productParams, configStore, location } = this.props;
    const ifShoppingCenter = configStore.siteConfig.isShoppingCenter;

    let params = null;
    if (productParams) {
      params = productParams;
    } else if (location.search) {
      params = parse(location.search);
    }

    if (
      (ifShoppingCenter && !window.isSSR) ||
      !productStore.productStates.get(id)
    ) {
      productStore
        .loadProduct(id, params)
        .then((fixedId) => {
          this.onProductLoad(fixedId);
        })
        .catch((e) => {
          // Don't make error out of 404 product api response,
          // so sentry won't pick it up.
          if (e.response && e.response.status !== 404) {
            console.error(e);
          }
        });
    } else {
      this.onProductLoad();
    }
  };

  onProductLoad = (fixedId) => {
    const { analytics, productStore, routeService, match } = this.props;

    if (fixedId && !isEqual(match.params.id, fixedId)) {
      this.navigateTo(
        routeService.getProductPath(this.getFixedProduct(fixedId))
      );
    }
    const product = this.getMatchingProduct();
    if (product) {
      analytics.productDetail([
        { product, activeProductId: this.getActiveProductId() }
      ]);
      productStore.insertLastVisitedProduct(product.id);
      this.maybeUpdateActiveCategory();
      this.sendTrackingData();
    }
  };

  getActiveHierarchyCategory = () => {
    const { categoryStore } = this.props;
    const product = this.getMatchingProduct();

    // Return only one result.
    return product.allCategories.filter((category) => {
      if (category.id !== product.main_category_id) {
        if (
          category.hierarchy.findIndex(
            (filteredCategory) =>
              filteredCategory.id === categoryStore.latestActiveCategory.id
          ) !== -1
        ) {
          return true;
        }
      }
      return false;
    })[0];
  };

  getCategoryBySection = () => {
    const { sectionStore } = this.props;
    const product = this.getMatchingProduct();

    // Will return one result if the current hierarchy is not the main one.
    return product.allCategories.filter((category) => {
      if (category.id !== product.main_category_id) {
        if (
          category.hierarchy.findIndex((filteredCategory) =>
            sectionStore.activeSection.includesCategory(filteredCategory)
          ) !== -1
        ) {
          return true;
        }
      }
      return false;
    })[0];
  };

  maybeUpdateActiveCategory = () => {
    const { categoryStore, sectionStore } = this.props;
    const product = this.getMatchingProduct();

    if (product) {
      // Default category is products main category.
      let activeCategory = product.main_category_id;

      if (product.allCategories.length === 1) {
        categoryStore.setActiveCategory(activeCategory);
        return;
      }

      const mainCategory = categoryStore.findCategoryById(
        product.main_category_id
      );

      if (categoryStore.latestActiveCategory) {
        const activeIsMainCategory =
          mainCategory.hierarchy.findIndex(
            (category) => category.id === categoryStore.latestActiveCategory.id
          ) !== -1;

        // When currently active category is not its main category
        // the correct one needs to be scanned from product other categories.
        if (
          !activeIsMainCategory &&
          product.belongsToCategory(categoryStore.latestActiveCategory.id)
        ) {
          const targetCategory = this.getActiveHierarchyCategory();
          if (targetCategory) {
            activeCategory = targetCategory.id;
          }
        }
      } else {
        if (sectionStore.activeSection) {
          const target = this.getCategoryBySection();
          if (target) {
            activeCategory = target.id;
          }
        }
      }
      categoryStore.setActiveCategory(activeCategory);
    }
  };

  sendTrackingData = () => {
    const { trackingStore, categoryStore, configStore } = this.props;

    const product = this.getMatchingProduct();
    const actualProduct =
      product && product.getActualProduct(this.getActiveProductId());
    if (
      !configStore.activateSpotmore.enabled ||
      typeof document === 'undefined' ||
      !actualProduct ||
      !this.getActiveProductId()
    ) {
      return null;
    }

    if (configStore.activateSpotmore.enabled && window.crmtracker) {
      window.crmtracker.sendData();
    }

    trackingStore.setPageView({
      categoryId: categoryStore.activeCategory
        ? categoryStore.activeCategory.id
        : null,
      categoryName: categoryStore.activeCategory
        ? categoryStore.activeCategory.name
        : null,
      page: TrackingEventType.PRODUCT,
      parentCategoryId: categoryStore.activeCategory
        ? categoryStore.activeCategory.parent_id
        : null,
      productActive: typeof actualProduct !== 'undefined',
      productId: this.getActiveProductId(),
      productName: actualProduct.name,
      refererUrl: document.referrer,
      visitedUrl: window.location.href
    });
    trackingStore.postPageView();
  };

  navigateTo = (path) => {
    this.props.history.replace(path);
  };

  redirectToProduct = (product) => {
    const { routeService } = this.props;
    return <Redirect to={routeService.getProductPath(product)} />;
  };

  setActiveProductId = (activeProductId, queryParams) => {
    const { match, history } = this.props;
    const params = { ...match.params, activeProductId };
    history.replace(
      addParametersToPath(generatePath(match.path, params), queryParams)
    );
  };

  getMatchingProduct = (fixedId) => {
    return this.props.productStore.products.get(
      fixedId ? fixedId : this.getProductId()
    );
  };

  getFixedProduct = (fixedId) => {
    return this.props.productStore.products.get(fixedId);
  };

  // TODO
  // This function is a workaround for an issue regarding percent encoded
  // URLs in React Router and its history library.
  // It should be fixed in the next version of history > 4.9.0, which had
  // not yet been published at the time of this alternate solution;
  // when that version is out, this fix can be removed.
  //
  // See: https://github.com/ReactTraining/history/issues/505
  getProductId = () => {
    return fixPercentDecodingForParam(this.props.match.params.id);
  };

  // The same workaround is also applied here.
  getActiveProductId = () => {
    const { match } = this.props;
    const product = this.getMatchingProduct();

    let activeProductId;
    if (product.class === ProductClass.COLLECTION) {
      activeProductId = match.params.activeProductId;
    } else if (product.class === ProductClass.MULTI) {
      activeProductId = match.params.activeProductId;
    } else {
      activeProductId = product.id;
    }

    return fixPercentDecodingForParam(activeProductId);
  };

  getShareImageUrl = () => {
    const { locationContext } = this.props;
    const product = this.getMatchingProduct();
    if (!product || !product.images || product.images.length === 0) {
      return null;
    }
    const productImage = product.images[0];

    return productImage.sizes['full'].length > 0 &&
      productImage.sizes['full'].startsWith('http')
      ? productImage.sizes['full']
      : `${locationContext.protocol}//${locationContext.host}${
          productImage.sizes['full']
        }`;
  };

  getAdSearchParams = () => {
    const { sectionStore } = this.props;
    const product = this.getMatchingProduct();
    if (!product) {
      return null;
    }

    const commonAdParams = {};
    if (sectionStore.activeSection) {
      commonAdParams.section = sectionStore.activeSection.id;
    }
    if (product.main_category_id) {
      commonAdParams.category = product.main_category_id;
    }
    if (product.manufacturer_id) {
      commonAdParams.manufacturer = product.manufacturer_id;
    }
    return commonAdParams;
  };

  renderShareButtons = (media) => {
    const { locationContext } = this.props;

    return (
      <ShareButtons
        media={media}
        url={locationContext.href}
        title={window.document.title || ''}
      />
    );
  };

  getManufacturerId = () => {
    const product = this.getMatchingProduct();
    return product && product.manufacturer && product.manufacturer.id;
  };

  render() {
    const {
      cartStore,
      categoryStore,
      productStore,
      routeService,
      sectionStore,
      location,
      match
    } = this.props;

    const product = this.getMatchingProduct();
    const breadcrumbs = product
      ? routeService.transformBreadCrumbs(
          product.breadcrumbsForCategory(categoryStore.activeCategory)
        )
      : [];

    // If the product does not belong to the active section, redirect.
    if (
      product &&
      ((sectionStore.activeSection &&
        !product.belongsToSection(sectionStore.activeSection.id)) ||
        (!sectionStore.activeSection &&
          product.belongsToSomeSection(sectionStore.sectionIds)))
    ) {
      return <Redirect to={routeService.getProductPath(product)} />;
    }

    // If the product is MULTI_CHILD, redirect to parent
    if (product && product.class === ProductClass.MULTI_CHILD) {
      return (
        <Redirect
          to={routeService.getProductPath(
            product,
            product.pathWithActiveProductId(product.id)
          )}
        />
      );
    }

    if (product) {
      const activeProductId = this.getActiveProductId();
      const expectedPath =
        activeProductId && match.params.activeProductId
          ? product.pathWithActiveProductId(activeProductId)
          : product.path;

      if (!pathFragmentMatchesLocation(expectedPath, location.pathname)) {
        const url = `${routeService.getProductPath(product, expectedPath)}${
          location.search
        }${location.hash}`;
        return <Redirect to={url} />;
      }
    }

    const adSearchParams = this.getAdSearchParams();
    const shareImageUrl = this.getShareImageUrl();
    const shareButtons = this.renderShareButtons(shareImageUrl);

    // If active collection or multi item is not valid, redirect to parent.
    if (product && product.class === ProductClass.COLLECTION) {
      if (
        this.getActiveProductId() &&
        !product.collection.getItemWithProductId(this.getActiveProductId())
      ) {
        return this.redirectToProduct(product);
      }
    }
    if (product && product.class === ProductClass.MULTI) {
      if (
        this.getActiveProductId() &&
        !product.getActualProduct(this.getActiveProductId())
      ) {
        return this.redirectToProduct(product);
      }
    }

    return (
      <Page
        name={CommonPage.PRODUCT}
        className={classNames('ProductPage', this.getManufacturerId(product))}
        onDefinedTrackingPage={TrackingEventType.PRODUCT}
      >
        <ScrollReset key={this.getProductId()} />
        {product && (
          <NavigationAddToCartRow
            product={product}
            activeProductId={this.getActiveProductId()}
          />
        )}
        <Container className="ProductPage__container">
          <SidebarLayout
            sidebar={
              product && <DefaultSidebar bannerSearchParams={adSearchParams} />
            }
            content={
              <Fragment>
                <HelperNavigation
                  breadcrumbsPath={breadcrumbs}
                  withGoBackLink
                  rightSide={shareButtons}
                />
                <ContentForState
                  state={productStore.productStates.get(this.getProductId())}
                  error={productStore.lastError}
                  forLoaded={() => {
                    const queryParams = parse(location.search);
                    const matchingProduct = this.getMatchingProduct();
                    return (
                      <Fragment>
                        <GenericMeta name="twitter:card" content="summary" />
                        <GenericMeta
                          property="og:image"
                          content={shareImageUrl}
                        />
                        <GenericMeta
                          property="og:type"
                          content="product.item"
                        />
                        <ProductPageContent
                          shareButtons={shareButtons}
                          product={matchingProduct}
                          activeProductId={this.getActiveProductId()}
                          setActiveProductId={this.setActiveProductId}
                          queryParams={queryParams}
                          adSearchParams={adSearchParams}
                          cartLoaded={cartStore.state === RequestState.LOADED}
                        />
                      </Fragment>
                    );
                  }}
                />
                <ContentPopup searchParams={adSearchParams} />
              </Fragment>
            }
          />
        </Container>
      </Page>
    );
  }
}

ProductPage.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  trackingStore: modelOf(TrackingStore).isRequired,
  sectionStore: modelOf(SectionStore),
  productStore: modelOf(ProductStore),
  cartStore: modelOf(CartStore),
  categoryStore: modelOf(CategoryStore),
  match: RouterPropTypes.match.isRequired,
  history: RouterPropTypes.history.isRequired,
  location: RouterPropTypes.location.isRequired,
  locationContext: LocationContextPropType.isRequired,
  analytics: PropTypes.instanceOf(Analytics)
};

export default inject(
  'configStore',
  'routeService',
  'productStore',
  'cartStore',
  'categoryStore',
  'sectionStore',
  'analytics',
  'locationContext',
  'trackingStore'
)(ProductPage);



// WEBPACK FOOTER //
// ./src/pages/ProductPage/index.jsx